/**
 * The CI warm hand-off chart note — resource shape and derivations shared by the
 * Orbit card and the bots that create it.
 *
 * A `Communication` categorised `ci-warm-handoff-note`, deliberately distinct
 * from the intro-call note so the two cards are separately addressable in
 * queries, timeline filters and the CI access policy.
 *
 * `Communication.status` carries draft state: `in-progress` while the rep works
 * the call, `completed` once they hit Save to chart.
 */

import { Communication } from '@medplum/fhirtypes';
import {
  answerString,
  answerStrings,
  hasAnyRecordedValue,
  PacProcedureDate,
  PatientAccessCallAnswers,
  readAnswersPayload,
} from './patient-access-note';
import { HandoffOutcome, isPaidOutcome } from './patient-access-warm-handoff-script';
import { PAC_WARM_HANDOFF_ANSWERS_PAYLOAD_ID, CommunicationCategory } from './constants';

/** The procedure date the patient agreed to — the shape shared with the PN consultation. */
export type WarmHandoffProcedureDate = PacProcedureDate;

export interface WarmHandoffAnswers extends PatientAccessCallAnswers {
  procedureDate?: WarmHandoffProcedureDate;
}

export const EMPTY_WARM_HANDOFF_ANSWERS: WarmHandoffAnswers = { values: {} };

export function isWarmHandoffNote(communication: Pick<Communication, 'category'>): boolean {
  return Boolean(
    communication.category?.some((category) =>
      category.coding?.some((coding) => coding.code === CommunicationCategory.PAC_WARM_HANDOFF_NOTE)
    )
  );
}

/** A note is a draft until the rep saves it to the chart. */
export function isWarmHandoffDraft(communication: Pick<Communication, 'status'>): boolean {
  return communication.status !== 'completed';
}

/**
 * Reads the structured answers off the note. Returns empty answers rather than
 * throwing on malformed JSON — a card that renders blank is recoverable, a card
 * that crashes the timeline is not.
 */
export function parseWarmHandoffAnswers(communication: Pick<Communication, 'payload'>): WarmHandoffAnswers {
  const parsed = readAnswersPayload<WarmHandoffAnswers>(communication, PAC_WARM_HANDOFF_ANSWERS_PAYLOAD_ID);
  if (!parsed) return { ...EMPTY_WARM_HANDOFF_ANSWERS };

  return {
    values: parsed.values ?? {},
    ...(parsed.procedureDate ? { procedureDate: parsed.procedureDate } : {}),
  };
}

/** True once the rep has recorded anything. Drives the unworked-draft state. */
export function hasAnyWarmHandoffAnswer(answers: WarmHandoffAnswers): boolean {
  return Boolean(answers.procedureDate) || hasAnyRecordedValue(answers.values);
}

export function getWarmHandoffOutcome(answers: WarmHandoffAnswers): HandoffOutcome | undefined {
  return answerString(answers.values.close) as HandoffOutcome | undefined;
}

/**
 * The collapsed preview: the outcome, and nothing else.
 *
 * Deliberately narrower than the intro call's `outcome · likelihood · barrier`.
 * The hand-off card's design collapses to one value the timeline can be scanned
 * on; anything a coordinator has to act on is a task or a scheduled follow-up,
 * not a line of note text.
 */
export function buildWarmHandoffPreview(answers: WarmHandoffAnswers): {
  outcome?: string;
  /** Drives the check glyph; everything else takes the warning glyph. */
  paid: boolean;
} {
  const outcome = getWarmHandoffOutcome(answers);
  return { outcome, paid: isPaidOutcome(outcome) };
}

/**
 * Human-readable summary stored on the `text-content` payload, so timeline
 * search and any generic Communication renderer show something meaningful.
 *
 * Records which payment option the patient chose and whether payment completed —
 * never a card number. Payment credentials are not read aloud, typed into Orbit
 * or recorded on the card at all; the payment order is the only path.
 */
export function buildWarmHandoffSummary(answers: WarmHandoffAnswers): string {
  const lines: string[] = ['Warm hand-off'];

  if (answers.procedureDate) {
    const withName = answers.procedureDate.physicianName ? ` with ${answers.procedureDate.physicianName}` : '';
    lines.push(`Procedure date: ${answers.procedureDate.start}${withName}`);
  } else {
    // The declined-to-commit chip lands in `values` rather than as a resolved
    // date, and is worth recording as explicitly as an agreed one.
    const declined = answerString(answers.values.t_date);
    if (declined) lines.push(`Procedure date: ${declined}`);
  }

  const payment = answerString(answers.values.r_pay);
  if (payment) lines.push(`Payment option: ${payment}`);

  const reconnect = answerString(answers.values.n_when);
  if (reconnect) lines.push(`Reconnect: ${reconnect}`);

  const barriers = answerStrings(answers.values.n_why);
  if (barriers.length > 0) lines.push(`Barriers: ${barriers.join(', ')}`);

  const outcome = getWarmHandoffOutcome(answers);
  if (outcome) lines.push(`Outcome: ${outcome}`);

  return lines.join('\n');
}
